import { useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { GoogleLogin } from '@react-oauth/google'
import { toast } from 'sonner'
import { useAuth } from '../contexts/AuthContext'
import { Building2, Eye, EyeOff, Zap, BarChart3, Users } from 'lucide-react'
import Button from '../components/common/Button'
import { isDevMode, getDevAccounts, cacheDevSession, getCachedDevSession } from '../utils/devAccounts'
import { isTokenExpired } from '../utils/jwtDecoder'

const BuildingSkyline = () => (
  <svg
    viewBox="0 0 800 320"
    preserveAspectRatio="xMidYMax slice"
    className="absolute bottom-0 left-0 w-full"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Ground plane with subtle gradient */}
    <defs>
      <linearGradient id="groundGlow" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#FF6F0F" stopOpacity="0.06" />
        <stop offset="100%" stopColor="#FF6F0F" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="bldg1" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#334155" />
        <stop offset="100%" stopColor="#1E293B" />
      </linearGradient>
      <linearGradient id="bldg2" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#475569" />
        <stop offset="100%" stopColor="#334155" />
      </linearGradient>
      <linearGradient id="bldg3" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#3B4C63" />
        <stop offset="100%" stopColor="#283548" />
      </linearGradient>
    </defs>

    {/* Ambient ground glow */}
    <rect x="0" y="240" width="800" height="80" fill="url(#groundGlow)" />

    {/* === Building 1 — short wide, far left === */}
    <rect x="10" y="165" width="110" height="155" rx="2" fill="url(#bldg1)" />
    <rect x="10" y="165" width="110" height="3" fill="#475569" opacity="0.5" />
    {/* Windows — 4 col x 5 row */}
    {[0, 1, 2, 3].map(col => [0, 1, 2, 3, 4].map(row => (
      <rect
        key={`b1-${col}-${row}`}
        x={24 + col * 24}
        y={180 + row * 26}
        width={10}
        height={14}
        rx={1}
        fill={
          (col === 1 && row === 0) || (col === 3 && row === 2) || (col === 0 && row === 4)
            ? '#FF6F0F'
            : (col === 2 && row === 1) || (col === 0 && row === 3)
              ? '#FCD34D'
              : '#475569'
        }
        opacity={
          (col === 1 && row === 0) || (col === 3 && row === 2) || (col === 0 && row === 4)
            ? 0.5
            : (col === 2 && row === 1) || (col === 0 && row === 3)
              ? 0.2
              : 0.25
        }
      />
    )))}

    {/* === Building 2 — tall narrow === */}
    <rect x="140" y="70" width="90" height="250" rx="2" fill="url(#bldg2)" />
    <rect x="140" y="70" width="90" height="3" fill="#64748B" opacity="0.4" />
    {/* Roof structure */}
    <rect x="170" y="55" width="30" height="15" rx="1" fill="#475569" />
    <rect x="182" y="40" width="6" height="15" fill="#64748B" />
    <circle cx="185" cy="38" r="2.5" fill="#FF6F0F" opacity="0.8">
      <animate attributeName="opacity" values="0.8;0.3;0.8" dur="3s" repeatCount="indefinite" />
    </circle>
    {/* Windows — 3 col x 8 row */}
    {[0, 1, 2].map(col => [0, 1, 2, 3, 4, 5, 6, 7].map(row => (
      <rect
        key={`b2-${col}-${row}`}
        x={154 + col * 26}
        y={85 + row * 26}
        width={12}
        height={16}
        rx={1}
        fill={
          (col === 0 && row === 1) || (col === 2 && row === 3) || (col === 1 && row === 5) || (col === 0 && row === 7)
            ? '#FF6F0F'
            : (col === 1 && row === 0) || (col === 2 && row === 6) || (col === 0 && row === 4)
              ? '#FCD34D'
              : '#64748B'
        }
        opacity={
          (col === 0 && row === 1) || (col === 2 && row === 3) || (col === 1 && row === 5) || (col === 0 && row === 7)
            ? 0.55
            : (col === 1 && row === 0) || (col === 2 && row === 6) || (col === 0 && row === 4)
              ? 0.25
              : 0.2
        }
      />
    )))}

    {/* === Building 3 — medium, slightly overlapping === */}
    <rect x="245" y="130" width="105" height="190" rx="2" fill="url(#bldg3)" />
    <rect x="245" y="130" width="105" height="3" fill="#64748B" opacity="0.3" />
    {/* Windows — 3 col x 6 row */}
    {[0, 1, 2].map(col => [0, 1, 2, 3, 4, 5].map(row => (
      <rect
        key={`b3-${col}-${row}`}
        x={260 + col * 30}
        y={148 + row * 26}
        width={12}
        height={14}
        rx={1}
        fill={
          (col === 1 && row === 1) || (col === 0 && row === 3) || (col === 2 && row === 5)
            ? '#FF6F0F'
            : (col === 2 && row === 0) || (col === 1 && row === 4)
              ? '#FCD34D'
              : '#475569'
        }
        opacity={
          (col === 1 && row === 1) || (col === 0 && row === 3) || (col === 2 && row === 5)
            ? 0.5
            : (col === 2 && row === 0) || (col === 1 && row === 4)
              ? 0.2
              : 0.22
        }
      />
    )))}

    {/* === Building 4 — tallest, hero building === */}
    <rect x="370" y="30" width="130" height="290" rx="2" fill="url(#bldg2)" />
    <rect x="370" y="30" width="130" height="4" fill="#64748B" opacity="0.5" />
    {/* Crown detail */}
    <rect x="410" y="18" width="50" height="12" rx="1" fill="#475569" />
    <rect x="430" y="6" width="10" height="12" fill="#64748B" />
    <circle cx="435" cy="4" r="2" fill="#FF6F0F" opacity="0.9">
      <animate attributeName="opacity" values="0.9;0.4;0.9" dur="2.5s" repeatCount="indefinite" />
    </circle>
    {/* Windows — 4 col x 10 row */}
    {[0, 1, 2, 3].map(col => [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(row => (
      <rect
        key={`b4-${col}-${row}`}
        x={386 + col * 28}
        y={48 + row * 26}
        width={12}
        height={16}
        rx={1.5}
        fill={
          (col === 1 && row === 0) || (col === 3 && row === 2) || (col === 0 && row === 5) ||
          (col === 2 && row === 7) || (col === 1 && row === 9) || (col === 3 && row === 4)
            ? '#FF6F0F'
            : (col === 0 && row === 1) || (col === 2 && row === 3) || (col === 3 && row === 8) || (col === 1 && row === 6)
              ? '#FCD34D'
              : '#64748B'
        }
        opacity={
          (col === 1 && row === 0) || (col === 3 && row === 2) || (col === 0 && row === 5) ||
          (col === 2 && row === 7) || (col === 1 && row === 9) || (col === 3 && row === 4)
            ? 0.6
            : (col === 0 && row === 1) || (col === 2 && row === 3) || (col === 3 && row === 8) || (col === 1 && row === 6)
              ? 0.25
              : 0.18
        }
      />
    )))}

    {/* === Building 5 — medium short, far right === */}
    <rect x="520" y="150" width="100" height="170" rx="2" fill="url(#bldg1)" />
    <rect x="520" y="150" width="100" height="3" fill="#475569" opacity="0.4" />
    {/* Windows — 3 col x 5 row */}
    {[0, 1, 2].map(col => [0, 1, 2, 3, 4].map(row => (
      <rect
        key={`b5-${col}-${row}`}
        x={536 + col * 26}
        y={166 + row * 28}
        width={11}
        height={15}
        rx={1}
        fill={
          (col === 2 && row === 0) || (col === 0 && row === 2) || (col === 1 && row === 4)
            ? '#FF6F0F'
            : (col === 1 && row === 1) || (col === 2 && row === 3)
              ? '#FCD34D'
              : '#475569'
        }
        opacity={
          (col === 2 && row === 0) || (col === 0 && row === 2) || (col === 1 && row === 4)
            ? 0.45
            : (col === 1 && row === 1) || (col === 2 && row === 3)
              ? 0.2
              : 0.22
        }
      />
    )))}

    {/* === Building 6 — slim accent, far right edge === */}
    <rect x="640" y="110" width="70" height="210" rx="2" fill="url(#bldg3)" />
    {[0, 1].map(col => [0, 1, 2, 3, 4, 5, 6].map(row => (
      <rect
        key={`b6-${col}-${row}`}
        x={654 + col * 24}
        y={126 + row * 26}
        width={10}
        height={14}
        rx={1}
        fill={(col === 0 && row === 2) || (col === 1 && row === 5) ? '#FF6F0F' : '#475569'}
        opacity={(col === 0 && row === 2) || (col === 1 && row === 5) ? 0.45 : 0.2}
      />
    )))}

    {/* Ground line */}
    <rect x="0" y="318" width="800" height="2" fill="#334155" />
  </svg>
)

const FloatingShape = ({ className, size, delay, duration, shape = 'square' }) => (
  <div
    className={`absolute ${className}`}
    style={{
      width: size,
      height: size,
      animationDelay: delay,
      animationDuration: duration,
    }}
  >
    {shape === 'square' ? (
      <div
        className="w-full h-full rotate-45 rounded-sm animate-login-float"
        style={{
          background: 'linear-gradient(135deg, rgba(255,111,15,0.12), rgba(255,111,15,0.04))',
          border: '1px solid rgba(255,111,15,0.08)',
          animationDelay: delay,
          animationDuration: duration,
        }}
      />
    ) : (
      <div
        className="w-full h-full rounded-full animate-login-float"
        style={{
          background: 'linear-gradient(135deg, rgba(97,206,112,0.1), rgba(97,206,112,0.03))',
          border: '1px solid rgba(97,206,112,0.06)',
          animationDelay: delay,
          animationDuration: duration,
        }}
      />
    )}
  </div>
)

const FeaturePill = ({ icon: Icon, text, delay }) => (
  <div
    className="flex items-center gap-3 animate-login-fade-in opacity-0"
    style={{ animationDelay: delay }}
  >
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
      <Icon size={15} className="text-primary-400" />
    </div>
    <span className="text-dark-300 text-sm">{text}</span>
  </div>
)

const Login = () => {
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  })
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const { login, loginWithGoogle, restoreDevSession, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  // If already authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }))
    // Dismiss toasts when user starts typing
    toast.dismiss()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    // Basic validation
    if (!credentials.email || !credentials.password) {
      toast.error('Please enter both email and password')
      setLoading(false)
      return
    }

    const result = await login(credentials)

    if (result.success) {
      navigate('/', { replace: true })
      return
    }

    toast.error(result.error || 'Login failed')
    setLoading(false)
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true)
    const result = await loginWithGoogle(credentialResponse.credential)
    if (result.success) {
      navigate('/', { replace: true })
      return
    }
    toast.error(result.error || 'Google login failed')
    setLoading(false)
  }

  const handleGoogleError = () => {
    toast.error('Google sign-in was cancelled or failed. Please try again.')
  }

  return (
    <div className="flex min-h-screen bg-white">
      {/* ===== LEFT PANEL — Decorative (desktop only) ===== */}
      <aside className="hidden lg:flex lg:w-[56%] relative overflow-hidden flex-col justify-between"
        style={{
          background: 'linear-gradient(165deg, #0F172A 0%, #1E293B 40%, #162032 100%)',
        }}
      >
        {/* Dot grid texture */}
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: 'radial-gradient(circle, #94A3B8 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }}
        />

        {/* Floating geometric shapes */}
        <FloatingShape className="top-[15%] left-[12%]" size="60px" delay="0s" duration="8s" shape="square" />
        <FloatingShape className="top-[25%] right-[18%]" size="36px" delay="2.5s" duration="10s" shape="circle" />
        <FloatingShape className="top-[55%] left-[8%]" size="28px" delay="4s" duration="9s" shape="circle" />
        <FloatingShape className="top-[10%] right-[30%]" size="44px" delay="1s" duration="11s" shape="square" />
        <FloatingShape className="top-[42%] right-[10%]" size="52px" delay="3s" duration="7s" shape="square" />

        {/* Brand content — vertically centered */}
        <div className="relative z-10 flex-1 flex flex-col justify-center px-12 xl:px-16">
          {/* Logo + Wordmark */}
          <div
            className="flex items-center gap-4 animate-login-fade-in opacity-0"
            style={{ animationDelay: '0.1s' }}
          >
            <img src="/logo.png" alt="Reziland" className="h-14 w-auto drop-shadow-lg" />
            <h1 className="font-display text-[2.5rem] font-bold tracking-tight text-white leading-tight">
              Reziland<span className="text-primary-500">.</span>
            </h1>
          </div>

          <p
            className="mt-2 text-lg text-dark-400 font-light tracking-wide animate-login-fade-in opacity-0"
            style={{ animationDelay: '0.35s' }}
          >
            Property Management, Simplified.
          </p>

          {/* Feature pills */}
          <div className="mt-10 space-y-4">
            <FeaturePill icon={Building2} text="Manage buildings effortlessly" delay="0.5s" />
            <FeaturePill icon={BarChart3} text="Real-time insights & analytics" delay="0.65s" />
            <FeaturePill icon={Users} text="Streamline resident communication" delay="0.8s" />
          </div>
        </div>

        {/* Building skyline at bottom */}
        <div className="relative w-full h-[280px] flex-shrink-0">
          <BuildingSkyline />
        </div>
      </aside>

      {/* ===== RIGHT PANEL — Login form ===== */}
      <main className="flex-1 flex flex-col lg:w-[44%] relative">
        {/* Orange accent bar — desktop */}
        <div className="hidden lg:block absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-primary-400 to-primary-600" />

        {/* Mobile header */}
        <div className="lg:hidden relative overflow-hidden"
          style={{
            background: 'linear-gradient(165deg, #0F172A 0%, #1E293B 100%)',
          }}
        >
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'radial-gradient(circle, #94A3B8 1px, transparent 1px)',
              backgroundSize: '20px 20px',
            }}
          />
          <div className="relative z-10 flex flex-col items-center py-10 px-6">
            <img src="/logo.png" alt="Reziland" className="h-12 w-auto drop-shadow-lg" />
            <h1 className="mt-3 font-display text-2xl font-bold tracking-tight text-white">
              Reziland<span className="text-primary-500">.</span>
            </h1>
            <p className="mt-1 text-sm text-dark-400">
              Property Management, Simplified.
            </p>
          </div>
          {/* Mini skyline silhouette for mobile */}
          <svg viewBox="0 0 400 40" preserveAspectRatio="xMidYMax slice" className="w-full h-8 -mb-px">
            <rect x="10" y="18" width="50" height="22" fill="#334155" rx="1" />
            <rect x="70" y="5" width="40" height="35" fill="#475569" rx="1" />
            <rect x="120" y="15" width="55" height="25" fill="#334155" rx="1" />
            <rect x="185" y="2" width="60" height="38" fill="#475569" rx="1" />
            <rect x="255" y="12" width="45" height="28" fill="#334155" rx="1" />
            <rect x="310" y="8" width="35" height="32" fill="#475569" rx="1" />
            <rect x="355" y="18" width="45" height="22" fill="#334155" rx="1" />
          </svg>
        </div>

        {/* Form area */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
          <div className="w-full max-w-[380px] space-y-8">
            {/* Heading */}
            <div className="animate-login-fade-in opacity-0" style={{ animationDelay: '0.1s' }}>
              <h2 className="font-display text-[1.75rem] font-bold tracking-tight text-dark-900 leading-tight">
                Welcome back
              </h2>
              <p className="mt-1.5 text-dark-400 text-[0.95rem]">
                Sign in to your admin account
              </p>
            </div>

            {/* Login form */}
            <form className="space-y-5 animate-login-fade-in opacity-0" style={{ animationDelay: '0.25s' }} onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-[0.8rem] font-semibold text-dark-500 mb-1.5 uppercase tracking-wider">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={credentials.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-[0.925rem] border border-slate-200 rounded-xl bg-white text-dark-900 placeholder-dark-300 shadow-sm transition-all duration-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none hover:border-slate-300"
                    placeholder="you@company.com"
                  />
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-[0.8rem] font-semibold text-dark-500 mb-1.5 uppercase tracking-wider">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={credentials.password}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-12 text-[0.925rem] border border-slate-200 rounded-xl bg-white text-dark-900 placeholder-dark-300 shadow-sm transition-all duration-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none hover:border-slate-300"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center group"
                    >
                      {showPassword ? (
                        <EyeOff size={18} className="text-dark-300 group-hover:text-dark-500 transition-colors" />
                      ) : (
                        <Eye size={18} className="text-dark-300 group-hover:text-dark-500 transition-colors" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-1">
                <Button
                  type="submit"
                  disabled={loading}
                  loading={loading}
                  variant="primary"
                  fullWidth
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>
              </div>
            </form>

            {/* Google Sign-In */}
            <div className="animate-login-fade-in opacity-0" style={{ animationDelay: '0.35s' }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-200 to-slate-200" />
                <span className="text-[0.7rem] font-semibold text-dark-300 uppercase tracking-widest">or continue with</span>
                <div className="flex-1 h-px bg-gradient-to-l from-transparent via-slate-200 to-slate-200" />
              </div>
              <div className="flex justify-center [&>div]:w-full [&_iframe]:!rounded-xl">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  theme="outline"
                  size="large"
                  shape="rectangular"
                  width="380"
                  logo_alignment="center"
                  text="signin_with"
                />
              </div>
            </div>

            {/* Dev Quick Login */}
            {isDevMode() && getDevAccounts().length > 0 && (
              <div className="animate-login-fade-in opacity-0" style={{ animationDelay: '0.4s' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-[0.7rem] font-semibold text-dark-400 uppercase tracking-widest">
                    Dev Quick Login
                  </span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="space-y-2">
                  {getDevAccounts().map((account) => (
                    <button
                      key={account.id}
                      disabled={loading}
                      onClick={async () => {
                        setLoading(true)
                        // Try cached session first
                        const cached = getCachedDevSession(account.email)
                        if (cached?.token && !isTokenExpired(cached.token)) {
                          if (restoreDevSession(cached)) {
                            navigate('/', { replace: true })
                            return
                          }
                        }
                        // Fall back to full login
                        const result = await login({ email: account.email, password: account.password })
                        if (result.success) {
                          cacheDevSession(account.email, result.data)
                          navigate('/', { replace: true })
                          return
                        }
                        toast.error(result.error || 'Login failed')
                        setLoading(false)
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl transition-all duration-200 disabled:opacity-50 group"
                    >
                      <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                        <Zap size={14} className="text-amber-500" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-dark-700 group-hover:text-dark-900 transition-colors">
                          {account.label}
                        </div>
                        <div className="text-xs text-dark-400">{account.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-4 text-center lg:text-left lg:px-12">
          <p className="text-xs text-dark-300">
            &copy; {new Date().getFullYear()} Reziland. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  )
}

export default Login
